// Shared currency formatting -- a single module-level value rather than
// prop-drilling through every page component. Fetched once at app mount (see
// App.js, same fetch-once-no-live-reactivity pattern already used for the
// tax rate) via GET /api/settings/currency; every other file that needs to
// format money imports formatCurrency from here instead of keeping its own
// hardcoded `toLocaleString(..., { currency: 'USD' })` copy.
//
// Every price value passed into formatCurrency is always raw USD -- that's
// what's actually stored (product.price, cart totals, order totals, etc.).
// `rate` is a display-only multiplier for currencies like SSP that move
// daily: the admin updates `rate` once (PUT /api/admin/settings/currency)
// and every price shown across the whole storefront re-converts immediately,
// without touching a single stored value.
let currentCurrency = { code: 'USD', symbol: '$', rate: 1 };

export function setCurrency(next) {
  if (!next || typeof next !== 'object') return;
  currentCurrency = {
    code: next.code || currentCurrency.code,
    symbol: next.symbol || currentCurrency.symbol,
    rate: Number.isFinite(next.rate) && next.rate > 0 ? next.rate : currentCurrency.rate,
  };
}

export function getCurrency() {
  return currentCurrency;
}

// A symbol ending in a letter is a code being read as a word ("SSP", "KES",
// "UGX"), and running it straight into the digits gives "SSP150,225.00",
// which has to be visually parsed before it can be read. Glyph symbols
// ($, £, €) have never needed the gap and don't get one.
const needsGap = (symbol) => /[A-Za-z]$/.test(symbol.trim());

// Non-breaking, deliberately. Prices sit in flex rows and narrow grid cells
// all over this app, and an ordinary space is a line-break opportunity: the
// code would wrap onto its own line, leaving a card reading "SSP" above
// "150,228". Doing it here fixes every price at once, rather than requiring
// white-space:nowrap on each of the two dozen classes that render money.
const GAP = String.fromCharCode(160); // U+00A0 -- built by code point so an invisible literal can never be 'tidied' into a normal space

// Whether the minor unit is worth showing.
//
// At an SSP rate near 7,800 a hundredth of a unit is about one hundred-
// thousandth of a cent, so ".00" is two characters of noise on every price in
// the app -- and these are the longest prices, the ones that overflow a card
// in the first place. Dropped only when the amount is a whole number, so a
// real fractional value is never rounded away or hidden: with an integer rate
// the conversion of a 2-decimal price is always whole, which is exactly the
// case this is for.
//
// The 1000 floor keeps small-denomination currencies untouched -- $15.00 is
// still $15.00, because there the minor unit is money a customer can spend.
const showsMinorUnit = (n) => !(Math.abs(n) >= 1000 && Number.isInteger(n));

// Manual symbol + toLocaleString (not Intl's `style: 'currency'`) so an
// admin-configured symbol doesn't have to be a real ISO 4217 code Intl
// recognizes -- only the thousands/decimal formatting comes from Intl.
export function formatCurrency(value) {
  const n = (Number(value) || 0) * currentCurrency.rate;
  const digits = showsMinorUnit(n) ? 2 : 0;
  const symbol = currentCurrency.symbol.trim();
  const gap = needsGap(symbol) ? GAP : '';
  return `${symbol}${gap}${n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
}
